import { useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { ModalButtonWithForm } from 'components/common';
import { ProductUserCustomAttributesForm } from '.';

import type { GetProductUserCustomAttributesRes } from 'shared-types/responses';
import type { CustomAttributesFormType } from './ProductUserCustomAttributesForm';

interface Props {
  attribute: GetProductUserCustomAttributesRes['customAttributes'][0];
  editAttributeSuccessText?: string;
  editAttributeText?: string;
  hideAnalyticsControl: boolean;
  updateData: () => Promise<void>;
  urlState: string;
}

const ProductUserCustomAttributesEdit = ({
  attribute,
  editAttributeSuccessText,
  editAttributeText,
  hideAnalyticsControl,
  updateData,
  urlState
}: Props) => {
  const { dispatch } = useDispatch();

  const { messages } = useReduxState();

  const [formIsVisible, setFormIsVisible] = useState(false);

  const handleSubmit = async (values: CustomAttributesFormType) => {
    await api.put(
      `/admin/product-user-custom-attributes/${urlState}/${attribute.id}`,
      // Default missing options to an empty array. Informed removes empty fields
      // from form state, so without this, deleting all options would cause the
      // previously saved options to be restored.
      { ...initialValues, ...values, options: values.options || [] }
    );
    dispatch.addNotice({
      text: editAttributeSuccessText || messages.attributesEditSuccess,
      type: 'success'
    });
    await updateData();
  };

  const initialValues = {
    editableBy: attribute.editableBy,
    includeInAnalytics: attribute.includeInAnalytics,
    isRequired: attribute.isRequired,
    max: attribute.max,
    min: attribute.min,
    options: attribute.options || [],
    title: attribute.title,
    titleTranslations: attribute.titleTranslations || {},
    type: attribute.type
  } as CustomAttributesFormType;

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonIcon="pen"
      buttonIsDropdown={true}
      buttonSize="header"
      buttonText={editAttributeText || messages.attributesEdit}
      initialValues={initialValues}
      modalActionText={messages.commonSaveChanges}
      modalTitle={editAttributeText || messages.attributesEdit}
      modalWidth="medium"
      onHide={() => setFormIsVisible(false)}
      onModalVisible={() => setFormIsVisible(true)}
      onSubmit={handleSubmit}
    >
      {() =>
        formIsVisible ? (
          <ProductUserCustomAttributesForm
            hideAnalyticsControl={hideAnalyticsControl}
            urlState={urlState}
          />
        ) : null
      }
    </ModalButtonWithForm>
  );
};

export default ProductUserCustomAttributesEdit;
